import { isReadableAtom } from "../util/nanostores.js";
import { isGetHook, isMutatorHook, isStore } from "./client.js";
import { atom } from "nanostores";
import { useStore } from "@nanostores/solid";
import { createEffect } from "solid-js";

//#region src/client/solid.ts
/**
* Type guard to check if a value is a SolidJS Accessor.
*
* @private
*/
function isAccessor(value) {
	return typeof value === "function";
}
/**
* Converts a SolidJS Accessor to a NanoStore Atom.
*
* This is used to convert SolidJS accessors to atoms, so that we can use them in the store.
*
* @private
*/
function accessorToAtom(accessor) {
	const a = atom(accessor());
	createEffect(() => {
		a.set(accessor());
	});
	return a;
}
function createSolidHook(hook) {
	return ({ path, query } = {}) => {
		const pathParams = {};
		const queryParams = {};
		for (const [key, value] of Object.entries(path ?? {})) {
			const v = value;
			pathParams[key] = isAccessor(v) ? accessorToAtom(v) : v;
		}
		for (const [key, value] of Object.entries(query ?? {})) {
			const v = value;
			queryParams[key] = isAccessor(v) ? accessorToAtom(v) : v;
		}
		const storeValue = useStore(hook.store({
			path: pathParams,
			query: queryParams
		}));
		return {
			data: () => storeValue().data,
			loading: () => storeValue().loading,
			error: () => storeValue().error
		};
	};
}
function createSolidMutator(hook) {
	return () => {
		const store = useStore(hook.mutatorStore);
		const mutate = async (args) => {
			const { body, path, query } = args;
			const pathParams = {};
			const queryParams = {};
			for (const [key, value] of Object.entries(path ?? {})) {
				const v = value;
				pathParams[key] = isAccessor(v) ? v() : v;
			}
			for (const [key, value] of Object.entries(query ?? {})) {
				const v = value;
				queryParams[key] = isAccessor(v) ? v() : v;
			}
			return hook.mutatorStore.mutate({
				body,
				path: pathParams,
				query: queryParams
			});
		};
		return {
			mutate,
			data: () => store().data,
			loading: () => store().loading,
			error: () => store().error
		};
	};
}
function createSolidStore(hook) {
	if (isReadableAtom(hook.obj)) return useStore(hook.obj);
	const result = {};
	for (const key in hook.obj) {
		if (!Object.prototype.hasOwnProperty.call(hook.obj, key)) continue;
		const value = hook.obj[key];
		if (isReadableAtom(value)) result[key] = useStore(value);
		else result[key] = value;
	}
	return () => result;
}
function useFragno(clientObj) {
	const result = {};
	for (const key in clientObj) {
		if (!Object.prototype.hasOwnProperty.call(clientObj, key)) continue;
		const hook = clientObj[key];
		if (isGetHook(hook)) result[key] = createSolidHook(hook);
		else if (isMutatorHook(hook)) result[key] = createSolidMutator(hook);
		else if (isStore(hook)) result[key] = createSolidStore(hook);
		else result[key] = hook;
	}
	return result;
}

//#endregion
export { accessorToAtom, isAccessor, useFragno };
//# sourceMappingURL=solid.js.map